import { z } from 'zod';
import { organizationIdSchema } from './organizations';
// KIR-5407: shared devices. The system helper enrolls a machine once with the
// org's MDM key and gets a device key; at each sign-in it trades the device key
// plus the OS identity for that person's own key on that machine.
/** hex(sha256("kirin-hw-v1:" + raw hardware ID)); the raw ID never leaves the machine. */
export const hardwareDeviceIdSchema = z
    .string()
    .regex(/^[a-f0-9]{64}$/, 'Expected a lowercase 64-character hex string');
export const enrollDeviceRequestSchema = z.object({
    hardwareDeviceId: hardwareDeviceIdSchema,
    hostname: z.string().min(1).max(255).optional(),
    osString: z.string().min(1).max(128).optional(),
    agentVersion: z.string().min(1).max(64).optional(),
    /**
     * The machine's current device key, if it still has one. Optional: the MDM
     * key alone re-enrolls an enrolled machine and rotates its key (KIR-5537).
     */
    currentDeviceKey: z
        .string()
        .regex(/^kirind_[a-f0-9]{64}$/)
        .optional(),
});
export const enrollDeviceResponseSchema = z.object({
    deviceId: z.string().uuid(),
    /** The device's own key (`kirind_…`). Shown once; the helper stores it system-only. */
    deviceKey: z.string(),
    organizationId: organizationIdSchema,
});
/**
 * Where the OS account comes from. Directory kinds identify the same person on
 * every machine; `local` and `guest` are scoped to one machine.
 */
export const osIdentityKindSchema = z.enum([
    'entra', // Entra ID object ID (from the S-1-12-1 SID)
    'ad', // Active Directory domain SID
    'macos_idp', // macOS Platform SSO / Jamf Connect identity
    'sssd', // Linux SSSD objectSID
    'local', // local account on this machine
    'guest', // guest or kiosk session on this machine
]);
/** Kinds whose identity is scoped to a single machine. */
export const DEVICE_SCOPED_IDENTITY_KINDS = [
    'local',
    'guest',
];
export const osIdentitySchema = z.object({
    kind: osIdentityKindSchema,
    /** SID, object ID or account name; ignored for `guest`. */
    value: z.string().min(1).max(256),
    /** OS account name to show, e.g. `CORP\alice` or `trainee01`. */
    displayName: z.string().min(1).max(256).optional(),
    /**
     * KIR-5670: for a `guest` that is a reclassified local account (a Windows
     * kiosk), that account's `local` value (its SID); its earlier local person
     * on this device folds into the device Guest. Ignored for other kinds.
     */
    localValue: z.string().min(1).max(256).optional(),
});
export const deviceHandshakeRequestSchema = z.object({
    osIdentity: osIdentitySchema,
    /** Work email when the helper found one; omitted when not found or skipped. */
    email: z.string().email().max(320).optional(),
});
/** How the person behind the OS identity was resolved. */
export const deviceIdentityAttributionSchema = z.enum([
    'member', // a member of the organization
    'discovered', // an email with no Kirin account
    'unattributed', // no email: a person named after the OS account
]);
export const deviceHandshakeResponseSchema = z.object({
    userApiKey: z.string(),
    userId: z.string().uuid(),
    organizationId: organizationIdSchema,
    attribution: deviceIdentityAttributionSchema,
});
export const deviceSelfResponseSchema = z.object({
    deviceId: z.string().uuid(),
    organizationId: organizationIdSchema,
    hostname: z.string().nullable(),
    enrolledAt: z.string().datetime(),
});
//# sourceMappingURL=devices.js.map